import { PATH_DASHBOARD } from 'src/routes/paths';

export const MARKETPLACE_FEE_RATE = 0.05;

// Stripe's standard card processing rate for direct charges. Sellers pay this
// out of their settlement, on top of the Careit fee.
export const STRIPE_FEE_RATE = 0.029;
export const STRIPE_FEE_FIXED_CENTS = 30;

/**
 * Estimate Stripe's processing fee on a charge total. This is an ESTIMATE —
 * actual processing fees are set by Stripe and vary by card (e.g.
 * international or premium cards cost more). Returns null for null/invalid
 * input.
 */
export function estimateStripeFeeCents(totalCents) {
  if (
    totalCents === null ||
    totalCents === undefined ||
    !Number.isFinite(Number(totalCents))
  ) {
    return null;
  }

  return (
    Math.round(Number(totalCents) * STRIPE_FEE_RATE) + STRIPE_FEE_FIXED_CENTS
  );
}

// Shared chip treatment for marketplace surfaces: softly squared corners
// (≈6px) instead of the theme's fully-rounded pills. Spread into a Chip's
// `sx` so local overrides can still win.
export const MARKETPLACE_CHIP_SX = { borderRadius: 0.75 };

// Canonical, sellable marketplace food types.
// 'prepared' is intentionally excluded: prepared food is broadly taxable in
// WA (and hot prepared food in CA), so it cannot satisfy the tax-exempt
// attestation sellers sign (D9).
export const MARKETPLACE_FOOD_TYPE_ALIASES = {
  DAIRY: 'dairy',
  MEAT: 'meat',
  PRODUCE: 'produce',
  'NON-PERISHABLE': 'non-perishable',
  BAKED: 'baked',
  BAKERY: 'baked',
  BEVERAGES: 'beverages',
  FROZEN: 'frozen',
};

export const MARKETPLACE_FOOD_TYPE_IDS = new Set([
  'dairy',
  'meat',
  'produce',
  'non-perishable',
  'baked',
  'beverages',
  'frozen',
]);

export function isMarketplaceListing(listing) {
  return (
    listing?.source_type === 'marketplace' ||
    listing?.listing_type === 'marketplace'
  );
}

export function formatCurrencyFromCents(cents) {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(cents) / 100);
}

export function formatMarketplacePrice(listing, options = {}) {
  const { t } = options;
  if (!isMarketplaceListing(listing)) {
    return null;
  }

  const total = formatCurrencyFromCents(listing.total_price_cents);
  if (listing.pricing_type === 'per_item') {
    const unit =
      listing.unit_label || (t ? t('marketplace.units.item') : 'item');
    const price = formatCurrencyFromCents(listing.price_per_unit_cents);
    return t
      ? t('marketplace.price-summary-with-unit', { total, price, unit })
      : `${total} total, ${price} per ${unit}`;
  }

  if (listing.pricing_type === 'per_weight') {
    const unit = listing.weight_unit || (t ? t('marketplace.units.lb') : 'lb');
    const price = formatCurrencyFromCents(listing.price_per_unit_cents);
    return t
      ? t('marketplace.price-summary-with-unit', { total, price, unit })
      : `${total} total, ${price} per ${unit}`;
  }

  return total;
}

export function calculateMarketplaceCheckoutAmounts(listing, quantity = null) {
  if (!listing) {
    return {
      subtotalCents: 0,
      platformFeeCents: 0,
      sellerAmountCents: 0,
      totalCents: 0,
    };
  }

  const pricingType = listing.pricing_type;
  const normalizedQuantity =
    quantity === null || quantity === undefined ? null : Number(quantity);

  let subtotalCents = Number(listing.total_price_cents || 0);
  if (
    ['per_item', 'per_weight'].includes(pricingType) &&
    Number.isFinite(normalizedQuantity) &&
    normalizedQuantity > 0
  ) {
    subtotalCents = Math.round(
      normalizedQuantity * Number(listing.price_per_unit_cents || 0)
    );
  }

  const platformFeeCents = Math.round(subtotalCents * MARKETPLACE_FEE_RATE);

  return {
    subtotalCents,
    platformFeeCents,
    sellerAmountCents: subtotalCents - platformFeeCents,
    totalCents: subtotalCents,
  };
}

export function calculateMarketplaceSellerPayoutCents(
  subtotalCents,
  deliveryFeeCents = 0
) {
  // The Careit fee applies to everything the seller collects — goods AND any
  // delivery fee — matching the backend buildOrderAmounts. Pass deliveryFeeCents
  // for delivery orders; omit it (defaults to 0) for the goods-only estimate.
  const subtotal = Math.round(Number(subtotalCents) || 0);
  const delivery = Math.round(Number(deliveryFeeCents) || 0);
  const total = subtotal + delivery;
  if (!Number.isFinite(total) || total <= 0) {
    return null;
  }

  return total - Math.round(total * MARKETPLACE_FEE_RATE);
}

export function getListingDetailPath(id, listing) {
  return isMarketplaceListing(listing)
    ? PATH_DASHBOARD.marketplace.viewListing(id)
    : PATH_DASHBOARD.viewDonation(id);
}

function normalizeMarketplacePriceCents(value, { allowZero = true } = {}) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  if (!allowZero && numericValue <= 0) {
    return null;
  }

  return numericValue;
}

export function getMarketplacePriceFilters(filters = {}) {
  return {
    minPriceCents: normalizeMarketplacePriceCents(filters.minPriceCents, {
      allowZero: false,
    }),
    maxPriceCents: normalizeMarketplacePriceCents(filters.maxPriceCents),
  };
}

export function hasMarketplacePriceFilter(filters = {}) {
  const { minPriceCents, maxPriceCents } = getMarketplacePriceFilters(filters);
  return minPriceCents != null || maxPriceCents != null;
}

// Display helpers: org data stores regions as either full names or codes,
// and the backend accepts both — but the UI should always SHOW full names.
export const US_STATE_CODE_TO_NAME = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export function stateDisplayName(code) {
  return US_STATE_CODE_TO_NAME[code] || code;
}

const US_STATE_NAME_TO_CODE = Object.fromEntries(
  Object.entries(US_STATE_CODE_TO_NAME).map(([code, name]) => [
    name.toLowerCase(),
    code,
  ])
);

/**
 * Normalize a US state to its 2-letter code. Accepts either a code ("WA",
 * any case) or a full name ("Washington" — what Google Places returns for
 * `administrative_area_level_1`). Returns null when unrecognized, so callers
 * can skip client-side checks and let the backend be the backstop.
 */
export function normalizeUsState(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const upper = trimmed.toUpperCase();
  if (US_STATE_CODE_TO_NAME[upper]) {
    return upper;
  }

  return US_STATE_NAME_TO_CODE[trimmed.toLowerCase()] ?? null;
}

const US_COUNTRY_VALUES = new Set([
  'US',
  'USA',
  'UNITED STATES',
  'UNITED STATES OF AMERICA',
]);

// An empty/unset country is treated as US (the platform default today).
export function isUsCountryValue(value) {
  const trimmed = (value ?? '').toString().trim();
  return !trimmed || US_COUNTRY_VALUES.has(trimmed.toUpperCase());
}

/**
 * Decide whether a chosen delivery destination falls outside what the seller
 * delivers to, mirroring the backend's 403 so the buyer is warned before an
 * order is created. Fails CLOSED: a US seller delivers only within their own
 * state, so any destination we can't positively confirm is in that state — a
 * non-US address, or a US state/territory we can't normalize (PR/GU/VI/AA/AE,
 * an unmapped string) — counts as a mismatch rather than slipping through.
 *
 * Returns null when there's no mismatch (or when the seller's own state is
 * unknown, so the client can't enforce and the server stays the backstop),
 * otherwise `{ type: 'state', stateCode }` or `{ type: 'country' }`.
 */
export function getDeliveryRegionMismatch({
  sellerCountry,
  sellerProvince,
  pickedCountry,
  pickedCountryCode,
  pickedProvince,
}) {
  if (isUsCountryValue(sellerCountry)) {
    const sellerStateCode = normalizeUsState(sellerProvince);
    if (!sellerStateCode) {
      return null;
    }

    const pickedIsUs = isUsCountryValue(pickedCountryCode || pickedCountry);
    const pickedStateCode = normalizeUsState(pickedProvince);
    if (!pickedIsUs || pickedStateCode !== sellerStateCode) {
      return { type: 'state', stateCode: sellerStateCode };
    }

    return null;
  }

  const sellerCountryNorm = (sellerCountry ?? '')
    .toString()
    .trim()
    .toLowerCase();
  const pickedCountryNorm = (pickedCountry ?? '')
    .toString()
    .trim()
    .toLowerCase();
  const pickedCountryCodeNorm = (pickedCountryCode ?? '')
    .toString()
    .trim()
    .toLowerCase();
  if (
    !sellerCountryNorm ||
    pickedCountryNorm === sellerCountryNorm ||
    (pickedCountryCodeNorm && pickedCountryCodeNorm === sellerCountryNorm)
  ) {
    return null;
  }

  return { type: 'country' };
}

let regionDisplayNames = null;
export function countryDisplayName(code, locale = 'en') {
  if (!code) return code;
  try {
    if (!regionDisplayNames) {
      regionDisplayNames = new Intl.DisplayNames([locale], {
        type: 'region',
      });
    }
    return regionDisplayNames.of(code) || code;
  } catch {
    return code;
  }
}
